import React, { useEffect, useState } from 'react';
import { View, Text, StyleSheet, ScrollView, ActivityIndicator } from 'react-native';
import { useTheme } from '../src/context/ThemeContext';
import type { ColorTheme } from '../src/utils/theme';
import { spacing, fontSize, fontWeight, borderRadius } from '../src/utils/theme';
import ContentContainer from '../src/components/ContentContainer';
import { api } from '../src/api/client';

interface CmsSubsection {
  title: string;
  content: string[];
}

interface CmsSection {
  title: string;
  content: string[];
  subsections?: CmsSubsection[];
}

interface CmsPage {
  slug: string;
  title: string;
  version: string | null;
  lastUpdated: string | null;
  sections: CmsSection[];
}

/** TermsScreen — Terms of Service fetched from the CMS at /api/cms/pages/terms-of-service. */
export default function TermsScreen() {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const [page, setPage] = useState<CmsPage | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const ac = new AbortController();
    api<CmsPage>('/api/cms/pages/terms-of-service', { skipAuth: true })
      .then((d) => { if (!ac.signal.aborted) setPage(d); })
      .catch((err) => { if (!ac.signal.aborted) console.error('fetch terms:', err); })
      .finally(() => { if (!ac.signal.aborted) setLoading(false); });
    return () => ac.abort();
  }, []);

  if (loading) return <View style={styles.container}><ActivityIndicator size="large" color={colors.primary} style={{ marginTop: 60 }} /></View>;

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <ContentContainer maxWidth={720}>
        <Text style={styles.title}>{page?.title || 'Terms of Service'}</Text>
        {page?.lastUpdated && (
          <Text style={styles.updated}>
            Last updated: {page.lastUpdated}{page.version ? ` (v${page.version})` : ''}
          </Text>
        )}
        {page?.sections.map((s, i) => (
          <View key={i} style={styles.section}>
            <Text style={styles.sectionTitle}>{s.title}</Text>
            {s.content.map((p, j) => (
              <Text key={j} style={styles.sectionContent}>{p}</Text>
            ))}
            {s.subsections?.map((sub, k) => (
              <View key={k} style={styles.subsection}>
                <Text style={styles.subsectionTitle}>{sub.title}</Text>
                {sub.content.map((p, j) => (
                  <Text key={j} style={styles.sectionContent}>{p}</Text>
                ))}
              </View>
            ))}
          </View>
        ))}
        <Text style={styles.copyright}>{'\u00A9'} 2026 RinsePay. All rights reserved.</Text>
        <View style={{ height: spacing.xxl }} />
      </ContentContainer>
    </ScrollView>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background },
  content: { padding: spacing.lg },
  title: { fontSize: fontSize.xxl, fontWeight: fontWeight.bold, color: c.text, marginBottom: spacing.xs },
  updated: { fontSize: fontSize.sm, color: c.textSecondary, marginBottom: spacing.lg },
  section: { marginBottom: spacing.lg, backgroundColor: c.surface, borderRadius: borderRadius.md, padding: spacing.md, borderWidth: 1, borderColor: c.borderLight },
  sectionTitle: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text, marginBottom: spacing.sm },
  sectionContent: { fontSize: fontSize.sm, color: c.textSecondary, lineHeight: 22, marginBottom: spacing.sm },
  subsection: { marginTop: spacing.md, paddingLeft: spacing.md, borderLeftWidth: 2, borderLeftColor: c.borderLight },
  subsectionTitle: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.text, marginBottom: spacing.xs },
  copyright: { fontSize: fontSize.xs, color: c.textLight, textAlign: 'center', marginTop: spacing.lg },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
